import React from 'react';
import useSeo from '../lib/seo';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, X } from 'lucide-react';
import Reveal from '../components/home/Reveal';
import { scrollToEl } from '../components/home/v3/motion';
import { PhoneFrame, BrowserFrame } from '../components/psicoid/Frames';
import ScreenTour from '../components/psicoid/ScreenTour';
import {
  LINKS,
  HERO_SHOTS,
  FACTS,
  BEFORE,
  AFTER,
  STEPS,
  PSY_TOUR,
  PATIENT_TOUR,
  WEB_TOUR,
  PUBLIC_SHOTS,
  GALLERY,
  PALETTE,
  PRINCIPLES,
  STACK,
  DELIVERY,
} from '../data/psicoid';

const INDEX = [
  { id: 'o-que-e', label: 'O que é' },
  { id: 'como-funciona', label: 'Como funciona' },
  { id: 'profissional', label: 'Para a profissional' },
  { id: 'paciente', label: 'Para o paciente' },
  { id: 'web', label: 'Painel web' },
  { id: 'layout', label: 'Layout' },
  { id: 'por-dentro', label: 'Por dentro' },
];

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
    <path d="M16.36 12.9c.02-1.86 1.52-2.75 1.58-2.8-.86-1.26-2.2-1.43-2.68-1.45-1.14-.12-2.23.67-2.8.67-.58 0-1.47-.66-2.42-.64-1.24.02-2.39.72-3.03 1.84-1.3 2.25-.33 5.57.93 7.4.62.9 1.35 1.9 2.31 1.86.93-.04 1.28-.6 2.4-.6 1.12 0 1.44.6 2.42.58 1-.02 1.63-.91 2.24-1.81.71-1.04 1-2.05 1.02-2.1-.02-.01-1.96-.75-1.98-2.95zM14.6 7.05c.5-.62.85-1.47.75-2.33-.73.03-1.62.49-2.15 1.1-.47.54-.89 1.42-.78 2.25.82.06 1.66-.41 2.18-1.02z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden>
    <path d="M3.6 2.3c-.2.2-.32.52-.32.93v17.54c0 .41.12.73.33.93l.06.05 9.83-9.82v-.23L3.66 2.25l-.06.05zM17 16.18l-3.28-3.28v-.23l3.28-3.28.08.04 3.88 2.2c1.11.63 1.11 1.66 0 2.3l-3.88 2.2-.08.05zM13.72 12.9L3.6 23.02c.37.38.97.43 1.65.05l11.6-6.59-3.13-3.58zM5.25.93C4.57.55 3.97.6 3.6.98L13.72 11.1l3.13-3.58L5.25.93z" />
  </svg>
);

function StoreLinks() {
  return (
    <>
      <a className="btn-ghost pid-store" href={LINKS.appStore} target="_blank" rel="noopener noreferrer">
        <AppleIcon /> App Store
      </a>
      <a className="btn-ghost pid-store" href={LINKS.googlePlay} target="_blank" rel="noopener noreferrer">
        <PlayIcon /> Google Play
      </a>
    </>
  );
}

function PsicoID() {
  useSeo({
    title: 'PsicoID: app e painel web para psicólogos | Idealizer',
    description:
      'PsicoID reúne agenda, agendamento pelo paciente, registros de sessão, prontuário e financeiro num app iOS e Android e num painel web. Veja como funciona e como é o layout. Projeto da Idealizer.',
    path: '/apps/psicoid',
  });

  return (
    <main className="psicoid-page">
      {/* ───────── Hero ───────── */}
      <section className="pid-hero">
        <div className="pid-hero-glow" aria-hidden />
        <div className="container">
          <Link to="/nosso-trabalho" className="pg-back">
            <ArrowLeft size={16} strokeWidth={2} aria-hidden /> Nosso trabalho
          </Link>

          <div className="pid-hero-grid">
            <div className="pid-hero-head">
              <span className="section-eyebrow pid-eyebrow">Saúde mental · iOS, Android e Web</span>
              <h1 className="pid-hero-title">
                PsicoID
              </h1>
              <p className="pid-hero-tagline">A rotina clínica inteira num app só.</p>
              <p className="pid-hero-sub">
                Plataforma para psicólogas e psicólogos organizarem o consultório: a profissional abre
                a agenda, o paciente marca sozinho pelo celular, e registros de sessão, prontuário e
                financeiro ficam no mesmo lugar.
              </p>
              <div className="pid-hero-actions">
                <a className="btn-primary" href={LINKS.site} target="_blank" rel="noopener noreferrer">
                  Visitar o site <ArrowUpRight size={18} strokeWidth={2} aria-hidden />
                </a>
                <StoreLinks />
              </div>
            </div>

            {/* Visual principal: sem Reveal, para aparecer de imediato acima da dobra. */}
            <div className="pid-hero-stage">
              <div className="pid-hero-blob" aria-hidden />
              <BrowserFrame
                className="pid-hero-browser"
                src={HERO_SHOTS.browser}
                alt="Painel web do PsicoID, com a agenda do dia"
                url="psicoid.com.br/dashboard"
                eager
              />
              <PhoneFrame
                className="pid-hero-phone"
                src={HERO_SHOTS.phone}
                alt="Tela inicial do app PsicoID"
                eager
              />
            </div>
          </div>

          <dl className="pid-facts">
            {FACTS.map((f) => (
              <div className="pid-fact" key={f.label}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>

          <nav className="pid-index" aria-label="Nesta página">
            {INDEX.map((item) => (
              <button type="button" key={item.id} onClick={() => scrollToEl(`#${item.id}`)}>
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* ───────── O que é ───────── */}
      <section className="pid-section" id="o-que-e">
        <div className="container pid-about">
          <Reveal className="pid-about-copy">
            <span className="section-eyebrow pid-eyebrow">O que é</span>
            <h2 className="section-title">Quem atende não deveria ser a própria secretária.</h2>
            <p className="pid-lead">
              Boa parte do trabalho de um consultório de psicologia acontece fora da sessão: marcar e
              remarcar por mensagem, lembrar o paciente, anotar o atendimento, conferir quem pagou.
            </p>
            <p className="pid-lead">
              O PsicoID tira essa logística das mãos da profissional. Ela configura a agenda uma vez
              e manda o link do perfil. Dali em diante o paciente agenda pelo app, os dois recebem
              lembrete, e cada sessão termina com registro e pagamento anotados.
            </p>
          </Reveal>

          <Reveal className="pid-compare" delay={120}>
            <div className="pid-compare-col pid-compare-col--before">
              <h3>Sem o PsicoID</h3>
              <ul>
                {BEFORE.map((t) => (
                  <li key={t}>
                    <X size={16} strokeWidth={2.2} aria-hidden /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="pid-compare-col pid-compare-col--after">
              <h3>Com o PsicoID</h3>
              <ul>
                {AFTER.map((t) => (
                  <li key={t}>
                    <Check size={16} strokeWidth={2.4} aria-hidden /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── Como funciona ───────── */}
      <section className="pid-section pid-section--soft" id="como-funciona">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-eyebrow">Como funciona</span>
            <h2 className="section-title">Em quatro passos, a agenda anda sozinha.</h2>
            <p className="section-sub">
              A profissional cuida das pessoas. O app cuida de marcar, lembrar e registrar.
            </p>
          </Reveal>

          <ol className="pid-steps">
            {STEPS.map((s, i) => (
              <Reveal as="li" className="pid-step" key={s.n} delay={i * 90}>
                <div className="pid-step-copy">
                  <span className="pid-step-n" aria-hidden>
                    {s.n}
                  </span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
                <div className="pid-step-media">
                  <PhoneFrame src={s.img} alt={s.alt} />
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ───────── App: profissional ───────── */}
      <section className="pid-section pid-persona pid-persona--psy" id="profissional">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-persona-eyebrow">
              <span className="pid-dot" aria-hidden /> App · para a profissional
            </span>
            <h2 className="section-title">O consultório no bolso.</h2>
            <p className="section-sub">
              Agenda, pacientes, registros e financeiro no mesmo app. Escolha um recurso para ver a
              tela.
            </p>
          </Reveal>
          <Reveal>
            <ScreenTour items={PSY_TOUR} label="Recursos do app para a profissional" />
          </Reveal>
        </div>
      </section>

      {/* ───────── App: paciente ───────── */}
      <section className="pid-section pid-persona pid-persona--patient" id="paciente">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-persona-eyebrow">
              <span className="pid-dot" aria-hidden /> App · para o paciente
            </span>
            <h2 className="section-title">Da busca à sessão marcada, sem trocar mensagem.</h2>
            <p className="section-sub">
              O mesmo app, visto do outro lado: encontrar a profissional, conhecer o trabalho dela e
              reservar um horário.
            </p>
          </Reveal>
          <Reveal>
            <ScreenTour items={PATIENT_TOUR} label="Recursos do app para o paciente" reverse />
          </Reveal>
        </div>
      </section>

      {/* ───────── Painel web ───────── */}
      <section className="pid-section pid-section--soft pid-persona pid-persona--psy" id="web">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-persona-eyebrow">
              <span className="pid-dot" aria-hidden /> Painel web
            </span>
            <h2 className="section-title">A mesma clínica, na tela grande.</h2>
            <p className="section-sub">
              No computador do consultório, o painel em psicoid.com.br repete tudo o que o app faz,
              com mais espaço para a semana, o prontuário e os números do mês.
            </p>
          </Reveal>
          <Reveal>
            <ScreenTour items={WEB_TOUR} frame="browser" label="Telas do painel web" />
          </Reveal>

          <div className="pid-public">
            {PUBLIC_SHOTS.map((s, i) => (
              <Reveal className="pid-public-item" key={s.id} delay={i * 120}>
                <BrowserFrame src={s.img} alt={s.alt} url={s.url} />
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Layout ───────── */}
      <section className="pid-section" id="layout">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-eyebrow">Layout</span>
            <h2 className="section-title">Uma linguagem só, em todas as telas.</h2>
            <p className="section-sub">
              O PsicoID tem sistema de design próprio: um conjunto pequeno de cores, uma família
              tipográfica e regras que valem do app ao painel web.
            </p>
          </Reveal>
        </div>

        <Reveal className="pid-gallery" role="group" aria-label="Telas do app PsicoID">
          <ul className="pid-gallery-track" tabIndex={0}>
            {GALLERY.map((g) => (
              <li key={g.label}>
                <PhoneFrame src={g.img} alt={`Tela ${g.label} do app PsicoID`} />
                <span>{g.label}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <div className="container">
          <div className="pid-system">
            <Reveal className="pid-system-card">
              <h3 className="pid-system-label">Cores</h3>
              <ul className="pid-palette">
                {PALETTE.map((c) => (
                  <li key={c.hex}>
                    <span
                      className={`pid-swatch ${c.dark ? 'is-dark' : ''}`}
                      style={{ background: c.hex }}
                      aria-hidden
                    >
                      {c.hex}
                    </span>
                    <strong>{c.name}</strong>
                    <span>{c.role}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="pid-system-card pid-type" delay={120}>
              <h3 className="pid-system-label">Tipografia</h3>
              <p className="pid-type-sample" aria-hidden>
                Aa
              </p>
              <p className="pid-type-name">Poppins</p>
              <ul className="pid-type-weights" aria-label="Pesos usados">
                <li style={{ fontWeight: 800 }}>ExtraBold · títulos</li>
                <li style={{ fontWeight: 600 }}>SemiBold · rótulos e botões</li>
                <li style={{ fontWeight: 400 }}>Regular · texto</li>
              </ul>
            </Reveal>
          </div>

          <ul className="pid-principles">
            {PRINCIPLES.map((p, i) => (
              <Reveal as="li" key={p.title} delay={i * 80}>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </Reveal>
            ))}
          </ul>

          <p className="pid-note">
            As telas desta página usam dados fictícios: nenhum nome, CRP ou valor é de uma pessoa
            real.
          </p>
        </div>
      </section>

      {/* ───────── Por dentro ───────── */}
      <section className="pid-section pid-section--soft" id="por-dentro">
        <div className="container">
          <Reveal className="section-head">
            <span className="section-eyebrow pid-eyebrow">Por dentro</span>
            <h2 className="section-title">Três frentes, um time.</h2>
            <p className="section-sub">
              App, web e backend foram desenhados e desenvolvidos aqui, e seguem sendo evoluídos pelo
              mesmo time.
            </p>
          </Reveal>

          <ul className="pid-stack">
            {STACK.map((s, i) => (
              <Reveal as="li" className="pid-stack-card" key={s.title} delay={i * 100}>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ul className="pid-tags" aria-label="Tecnologias">
                  {s.tags.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </ul>

          <Reveal className="pid-delivery">
            <h3>O que a Idealizer fez</h3>
            <ul>
              {DELIVERY.map((d) => (
                <li key={d}>
                  <Check size={16} strokeWidth={2.4} aria-hidden /> {d}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="pid-cta">
        <div className="container">
          <Reveal className="cta-card">
            <div className="cta-glow" aria-hidden />
            <h2 className="cta-title">Quer um produto como o PsicoID?</h2>
            <p className="cta-sub">
              Projetamos, desenvolvemos e mantemos o PsicoID no ar, do design de interface à
              publicação nas lojas. Vamos tirar a sua ideia do papel.
            </p>
            <div className="cta-actions">
              <Link to="/contato" className="btn-primary">
                Começar um projeto <ArrowRight size={18} strokeWidth={2} aria-hidden />
              </Link>
              <a className="btn-ghost" href={LINKS.site} target="_blank" rel="noopener noreferrer">
                Conhecer o PsicoID
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default PsicoID;
